import { SelectionModel } from "@angular/cdk/collections";
import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  Output,
  ViewChild,
} from "@angular/core";
import { MatLegacyDialog as MatDialog } from "@angular/material/legacy-dialog";
import { MatLegacySnackBar as MatSnackBar } from "@angular/material/legacy-snack-bar";
import { Router } from "@angular/router";
import { CareplusNewCustomer } from "src/models/careplus-new-customer";
import { CareplusAgencyService } from "src/services/careplus-agency-service";
import { TableComponent } from "src/app/table.component";
import { ReportService } from "src/services/report.service";

@Component({
  selector: "app-employee-data-submitted",
  templateUrl: "./employee-data-submitted.component.html",
  styleUrls: ["./employee-data-submitted.component.css"],
})
export class EmployeeDataSubmittedComponent extends TableComponent<CareplusNewCustomer> {
  private _careplusAgencyService: CareplusAgencyService;
  private _dialog = inject(MatDialog);
  private _snackBar = inject(MatSnackBar);
  private _router = inject(Router);
  private _reportService = inject(ReportService);
  selection = new SelectionModel<any>(true, []);
  @Output() dataLoaded = new EventEmitter<any[]>();
  @Output() onUploadClick = new EventEmitter<void>();
  @Output() onCalculateAmount = new EventEmitter<number[]>();
  @Input() statusFilter: string | null = null;
  showViewPaymentLog: boolean = true;
  @ViewChild("input") input!: ElementRef<HTMLInputElement>;
  isTableDataLoading = true;
  search: string = "";
  isOpen = false;

  constructor(careplusAgencyService: CareplusAgencyService) {
    super(careplusAgencyService);
    this._careplusAgencyService = careplusAgencyService;
    this.dataSubject.subscribe((data) => {
      this.dataLoaded.emit(data);
    });
    this.loadingSubject.subscribe((isLoading) => {
      console.log("isLoading", isLoading);
      this.isTableDataLoading = isLoading;
    });
  }

  displayedColumns: string[] = [
    "select",
    "id",
    "firstName",
    "lastName",
    "employeeId",
    "gender",
    "dateOfBirth",
    "email",
    "phoneNumber",
    "importedAt",
    "policyStartDate",
    "policyEndDate",
    "basePremium",
    "totalFees",
    "totalPolicyAmount",
    "discountAmount",
    "amountPaid",
    "status",
    "userStatus",
    "actions",
  ];

  ngOnInit(): void {
    super.ngOnInit();
  }

  filter() {
    super.ngOnInit();
  }

  override getTableData() {
    return super.getTableData();
  }

  override getCount() {
    return this._careplusAgencyService.getCount({
      filter: this.search,
      ...(this.statusFilter ? { status: this.statusFilter } : {}),
    });
  }

  protected getData(): any {
    if (this.showViewPaymentLog) {
      return {
        skip: 0,
        take: 99999,
        filter: this.search,
        ...(this.statusFilter ? { status: this.statusFilter } : {}),
      };
    }
    let pageSize = this.paginator?.pageSize ? this.paginator.pageSize : 10;
    return {
      skip: (this.paginator?.pageIndex ?? 0) * pageSize,
      take: pageSize,
      filter: this.search,
      orderBy: this.sort?.active,
      orderByDescending: this.sort?.direction === "desc",
      ...(this.statusFilter ? { status: this.statusFilter } : {}),
    };
  }

  override isAllSelected() {
    const data = this.dataSubject.getValue();
    const numSelected = this.selection.selected.length;
    const numRows = data.length;
    return numSelected === numRows && numRows > 0;
  }

  override masterToggle() {
    if (this.isAllSelected()) {
      this.selection.clear();
    } else {
      const data = this.dataSubject.getValue();
      this.selection.clear();
      data.forEach((row) => this.selection.select(row));
    }
  }

  emitCalculateAmount(all: boolean = false) {
    const ids = all ? [] : this.selection.selected.map((e) => e.id);
    this.onCalculateAmount.emit(ids);
  }

  toggleActive(employee: CareplusNewCustomer, makeActive: boolean) {
    this._careplusAgencyService
      .activateDeactivate(employee.id, makeActive)
      .subscribe({
        next: () => {
          employee.active = makeActive;
        },
        error: (err) => {
          employee.active = !makeActive;
          this._snackBar.open(
            err ||
              `Failed to ${makeActive ? "activate" : "deactivate"} employee.`,
            "CLOSE",
            { horizontalPosition: "right", duration: 4000 },
          );
        },
      });
  }

  openPurchaseDetail(row) {
    const status = row?.status?.toUpperCase();

    if (status === "FORM_SUBMITED") {
      this._router.navigate(["/careplus/careplus-add-customers/" + row.id]);
    } else if (
      status === "PAYMENT_INITIATED" ||
      status === "PROCESSING" ||
      status === "POLICY_ISSUED"
    ) {
      this._router.navigate(["/careplus-orders/" + row.id]);
    }
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case "IMPORTED":
        return "Invite Pending";
      case "MESSAGE_SENT":
        return "Invited";
      case "FORM_SUBMITED":
        return "Data Submitted";
      case "PAYMENT_INITIATED":
        return "Payment Initiated";
      case "PROCESSING":
        return "Processing";
      case "POLICY_ISSUED":
        return "Policy Issued";
      default:
        return status ?? "-";
    }
  }

  onViewPaymentLog() {
    this._router.navigate(["/careplus/employee-payment-log"]);
  }
  onViewEmployeeList() {
    this._router.navigate(["/careplus/careplus-add-customers"]);
  }

  downloadReport(isXls: boolean) {
    this.isOpen = false;
    let pageSize = this.paginator?.pageSize ? this.paginator.pageSize : 10;
    const selectedIds = this.selection.selected.map((row) => row.id);
    const data = {
      skip: (this.paginator?.pageIndex ?? 0) * pageSize,
      take: pageSize,
      filter: this.search,
      orderBy: this.sort?.active,
      orderByDescending: this.sort?.direction === "desc",
      ids: selectedIds,
    };

    this._reportService.downloadAgencyHRSubmittedFormsReport(data, isXls);
  }
}
